import fileDownload from 'js-file-download';
import { isString } from 'lodash/lang';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React from 'react';
import { toast } from 'react-toastify';

import { downloadInstance } from '../services/api';
import { getPrinters } from '../services/printer';

export function baseValue(value, base = '') {
  if (Number.isInteger(value)) {
    return value;
  }

  if (!value) {
    return base === 'undefined' ? undefined : base;
  }

  return value;
}

export function objectToParam(obj = {}) {
  let arr = [];
  for (var key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (
        obj[key] &&
        obj[key] !== '' &&
        !(key === 'page[number]' && obj[key] === 1) &&
        !(key === 'page[size]' && parseInt(obj[key], 10) === 10)
      ) {
        arr.push(key + '=' + encodeURIComponent(obj[key]));
      }
    }
  }

  return arr.join('&');
}

// Stolen: https://davidwalsh.name/javascript-debounce-function
export function debounce(func, wait, immediate) {
  var timeout;
  return function () {
    var context = this,
      args = arguments;
    var later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    var callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export function dateRender(dateString) {
  const date = moment(dateString, 'YYYY-MM-DD hh:mm:ss');
  return <span data-tip={date.format('D/M/Y  hh:mm:ss')}>{date.format('D/M/Y')}</span>;
}

// Stolen: http://stackoverflow.com/a/26407251
export function fetchFromObject(obj, prop) {
  if (typeof obj === 'undefined') {
    return false;
  }

  var _index = prop.indexOf('.');
  if (_index > -1) {
    if (obj[prop.substring(0, _index)]) {
      return fetchFromObject(obj[prop.substring(0, _index)], prop.substr(_index + 1));
    }
  }

  return obj[prop];
}

export function userHasPermission(user, permissions = [], operator = '&&') {
  if (user.permissions) {
    let matches = 0;

    permissions.forEach((permission) => {
      const currentmatches = user.permissions.filter((userPermission) => userPermission.name.startsWith(permission));

      if (currentmatches.length > 0) {
        matches++;
      }
    });

    if (operator === '&&' && matches === permissions.length) {
      return true;
    }

    if (operator === '||' && matches > 0) {
      return true;
    }
  }

  return false;
}

export function goToTop() {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
}

function BasicErrorNotification(props) {
  return (
    <div>
      <p>
        <strong className="font-bold">{props.title}</strong>
      </p>
      {props.errors && <ul>{props.errors.map((errors) => errors.map((error) => <li key={error}>{error}</li>))}</ul>}
    </div>
  );
}

BasicErrorNotification.propTypes = {
  'title': PropTypes.string,
  'errors': PropTypes.array,
  'errors.map': PropTypes.func,
};

export function ajaxErrorHandler(e) {
  if (!e) {
    toast.error(
      <BasicErrorNotification title="Something went wrong. Please reload your browser and try again." errors={[]} />,
      {
        className: 'alert-danger alert',
      }
    );
    return false;
  }
  if (e.response && e.response.data) {
    const errors = e.response.data.errors ? Object.entries(e.response.data.errors).map((entry) => entry[1]) : [];

    toast.error(
      <BasicErrorNotification
        title={e.response.data.message ? e.response.data.message : e.response.data}
        errors={errors}
      />,
      {
        className: 'alert-danger alert',
      }
    );
  }
}

export function setErrorsOnForm(setError, e) {
  if (e.response?.data?.errors) {
    const errors = Object.entries(e.response.data.errors);
    errors.forEach(([field, errors]) => {
      errors.forEach((message) => setError(field, { message }));
    });
  }
}

export function defaultSuccess(message) {
  console.log(message);
  toast.success(message, {
    className: 'alert-success alert',
  });
}

// hacky handler to ahndle the wonkly errors throughout the app
export function superFunkyUniversalErrorHandler(e) {
  if (e.response && e.response.data) {
    const errors = e.response.data.errors ? Object.entries(e.response.data.errors).map((entry) => entry[1]) : [];

    toast.error(
      <BasicErrorNotification
        title={e.response.data.message ? e.response.data.message : e.response.data}
        errors={errors}
      />,
      {
        className: 'alert-danger alert',
      }
    );
    return false;
  }

  if (e.message || e.text) {
    toast.error(<BasicErrorNotification title={`Something went wrong: ${e.message ?? e.text}`} errors={[]} />, {
      className: 'alert-danger alert',
    });
    return false;
  }

  if (isString(e)) {
    toast.error(<BasicErrorNotification title={`Something went wrong: ${e}`} errors={[]} />, {
      className: 'alert-danger alert',
    });
    return false;
  }

  if (!e) {
    toast.error(
      <BasicErrorNotification title="Something went wrong. Please reload your browser and try again." errors={[]} />,
      {
        className: 'alert-danger alert',
      }
    );
    return false;
  }
}

export function defaultError(message, error) {
  console.log('error', message, error);
  if (error?.response?.data?.message) {
    return toast.error(error?.response?.data?.message, {
      className: 'alert-danger alert',
    });
  }
  toast.error(message, {
    className: 'alert-danger alert',
  });
}

export function defaultDayFormater(day) {
  return moment(day, 'YYYY-MM-DD').format('ddd DD/MM');
}

export function formatUTCDate(day, format) {
  return moment.utc(day).tz('Europe/Brussels').format(format);
}

export function getPrinterForDayPeriod(day, period, settings) {
  return new Promise(function (resolve, reject) {
    if (settings[period.id] && settings[period.id][day] && settings[period.id][day] !== '') {
      const printerName = settings[period.id][day];
      getPrinters().then((response) => {
        if (response.printer) {
          const printer = response.printer.filter((printer) => printer.name === printerName);

          if (printer.length > 0) {
            resolve(printer[0]);
          } else {
            defaultError('Printer not connected, please check printers page.');
            reject();
          }
        } else {
          defaultError('No Printers connected, please check printers page.');
          reject();
        }
      });
    } else {
      defaultError(
        `Printer not set for ${
          day === 'combi' ? day : defaultDayFormater(day)
        } ${period.name}, please check printers page.`
      );
      reject();
    }
  });
}

export function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function closest(el, selector) {
  let matchesFn;

  // find vendor prefix
  ['matches', 'webkitMatchesSelector', 'mozMatchesSelector', 'msMatchesSelector', 'oMatchesSelector'].some(
    function (fn) {
      if (typeof document.body[fn] === 'function') {
        matchesFn = fn;
        return true;
      }
      return false;
    }
  );

  let parent;

  // traverse parents
  while (el) {
    parent = el.parentElement;
    if (parent && parent[matchesFn](selector)) {
      return parent;
    }
    el = parent;
  }

  return null;
}

export function downloadFile(url, fileName) {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  downloadInstance({
    method: 'get',
    url: baseUrl + url,
    responseType: 'blob',
  })
    .then((response) => {
      fileDownload(response.data, fileName);
    })
    .catch(ajaxErrorHandler);
}

export const defaultCrewProps = {
  user: PropTypes.object,
  userCompany: PropTypes.object,
  userEvent: PropTypes.object,
  registration: PropTypes.object,
};
